// src/store/slices/employeeLocationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import employeeLocationApi from 'api/employeeLocationApi';

export const fetchEmployeeLocations = createAsyncThunk(
  'employeeLocation/fetchEmployeeLocations',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await employeeLocationApi.getAll(page, pageSize, search);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load locations');
    }
  }
);

export const createEmployeeLocation = createAsyncThunk(
  'employeeLocation/createEmployeeLocation',
  async (data, { rejectWithValue }) => {
    try {
      const response = await employeeLocationApi.create(data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create location');
    }
  }
);

export const updateEmployeeLocation = createAsyncThunk(
  'employeeLocation/updateEmployeeLocation',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await employeeLocationApi.update(id, data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update location');
    }
  }
);

export const deleteEmployeeLocation = createAsyncThunk(
  'employeeLocation/deleteEmployeeLocation',
  async (id, { rejectWithValue }) => {
    try {
      await employeeLocationApi.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || err.response?.data?.detail || 'Failed to delete location'
      );
    }
  }
);

export const exportEmployeeLocations = createAsyncThunk(
  'employeeLocation/exportEmployeeLocations',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await employeeLocationApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee-locations_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

const employeeLocationSlice = createSlice({
  name: 'employeeLocation',
  initialState: {
    locations: [],
    pagination: { count: 0, page_size: 10, total_pages: 1, current_page: 1 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeeLocations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeLocations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.locations = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchEmployeeLocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createEmployeeLocation.fulfilled, (state, action) => {
        state.locations.unshift(action.payload);
        state.pagination.count += 1;
      })

      .addCase(updateEmployeeLocation.fulfilled, (state, action) => {
        const idx = state.locations.findIndex(l => l.id === action.payload.id);
        if (idx !== -1) state.locations[idx] = action.payload;
      })

      .addCase(deleteEmployeeLocation.fulfilled, (state, action) => {
        state.locations = state.locations.filter(l => l.id !== action.payload);
        state.pagination.count -= 1;
      })

      .addCase(exportEmployeeLocations.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError } = employeeLocationSlice.actions;
export default employeeLocationSlice.reducer;